import {
  findStaticPage, mutateRemoveStaticPageBlock, mutateRemoveStaticPage,
  mutateRichTextBlock, mutateSearchBarBlock, mutateAnnouncementBlock, mutateStickersBlock,
  mutateOrganizationsBlock, mutateListBlock, mutateFeaturedQuestion, mutateQuestionBlock,
  mutateBannerBlock,
  mutateCollectionsBlock, mutateToggleStatusStaticPageBlock,
} from '@/graphs/definitions';
import globalShared from '@/shared';

// Types for static page data
export interface BlockData {
  id: string;
  sortOrder: number;
  blockType: string;
}

export interface RichTextBlockData {
  id: string;
  heading: string;
  text: string;
}

export interface BannerBlockData {
  id: string;
  setFullWidth?: boolean;
  items?: {
    imageUrl: string;
    deepLink: string;
    isEnabled: boolean;
  }[];
}

export interface SearchBarBlockData {
  id: string;
  searchType: string;
  placeholderText: string;
}

export interface AnnouncementBlockData {
  id: string;
  text: string;
  textColor: string;
  textBackgroundColor: string;
}

export interface StickersBlockData {
  id: string;
  title: string;
  columnsCount: number;
  stickersList: string[];
}

export interface OrganizationsBlockData {
  id: string;
  title: string;
  organizationsList: string[];
}

export interface ListBlockData {
  id: string;
  title: string;
  organizationsList: string[];
}

export interface QuestionBlockData {
  id: string;
  text: string;
  questionId: string;
  organizationId: string;
}

export interface CollectionsBlockData {
  id: string;
  title: string;
  groups: {
    title: string;
    coverImageUrl: string;
    organizationsList: string[];
    isEnabled: boolean;
  }[];
}

export interface OrganizationData {
  id: string;
  name: string;
  avatar?: string | null;
}

export interface StickerData {
  id: string;
  title: string;
  url: string;
  thumbnailUrl?: string;
  organization?: {
    id: string;
    name: string;
  };
  user?: {
    id: string;
    firstname: string;
    lastname: string;
  };
}

export interface StaticPageData {
  id: string;
  slug: string;
  title: string;
  blocks: BlockData[];
  richTextBlocks?: RichTextBlockData[];
  bannerBlocks?: BannerBlockData[];
  searchBarBlocks?: SearchBarBlockData[];
  announcementBlocks?: AnnouncementBlockData[];
  stickersBlocks?: StickersBlockData[];
  organizationsBlocks?: OrganizationsBlockData[];
  collectionsBlocks?: CollectionsBlockData[];
  listBlocks?: ListBlockData[];
  questionBlocks?: QuestionBlockData[];
  organizations?: OrganizationData[];
  stickers?: StickerData[];
}

export interface ProcessedBlock extends BlockData {
  details: RichTextBlockData | BannerBlockData | SearchBarBlockData | AnnouncementBlockData |
    StickersBlockData | OrganizationsBlockData | CollectionsBlockData | ListBlockData |
    QuestionBlockData | Record<string, any> | null;
}

/**
 * Utility functions for static page management
 */
export function useStaticPageUtils() {
  const { makeGraphRequest, getAuthToken } = globalShared();

  /**
   * Calculate the next available sortOrder for a new block
   * @param staticPageData - Static page data containing existing blocks
   * @returns Next available sortOrder (0-based indexing: numberOfBlocks)
   */
  function getNextSortOrder(staticPageData: StaticPageData): number {
    if (!staticPageData.blocks || staticPageData.blocks.length === 0) {
      return 0;
    }

    // Return the count of existing blocks (0-based indexing)
    return staticPageData.blocks.length;
  }

  /**
   * Process static page response data by combining blocks with their detailed content
   * @param staticPageData - Raw static page data from GraphQL response
   * @returns Array of processed blocks with combined data, sorted by sortOrder
   */
  function processStaticPageBlocks(staticPageData: StaticPageData): ProcessedBlock[] {
    if (!staticPageData || !staticPageData.blocks) {
      return [];
    }

    return staticPageData.blocks
      .map((block) => {
        let details: RichTextBlockData | BannerBlockData | Record<string, any> | null = null;

        // Find detailed content based on block type
        if (block.blockType === 'RichText' && staticPageData.richTextBlocks) {
          details = staticPageData.richTextBlocks.find((rtb) => rtb.id === block.id) || null;
        } else if (block.blockType === 'Banner' && staticPageData.bannerBlocks) {
          details = staticPageData.bannerBlocks.find((bb) => bb.id === block.id) || null;
        } else if (block.blockType === 'SearchBar' && staticPageData.searchBarBlocks) {
          details = staticPageData.searchBarBlocks.find((sb) => sb.id === block.id) || null;
        } else if ((block.blockType === 'AnnouncementBar' || block.blockType === 'Announcement')
          && staticPageData.announcementBlocks) {
          details = staticPageData.announcementBlocks.find((ab) => ab.id === block.id) || null;
        } else if (block.blockType === 'Stickers' && staticPageData.stickersBlocks) {
          details = staticPageData.stickersBlocks.find((sb) => sb.id === block.id) || null;
        } else if (block.blockType === 'Organizations' && staticPageData.organizationsBlocks) {
          details = staticPageData.organizationsBlocks.find((ob) => ob.id === block.id) || null;
        } else if (block.blockType === 'Collections' && staticPageData.collectionsBlocks) {
          details = staticPageData.collectionsBlocks.find((cb) => cb.id === block.id) || null;
        } else if (block.blockType === 'List' && staticPageData.listBlocks) {
          details = staticPageData.listBlocks.find((lb) => lb.id === block.id) || null;
        } else if (block.blockType === 'Question' && staticPageData.questionBlocks) {
          details = staticPageData.questionBlocks.find((qb) => qb.id === block.id) || null;
        } else {
          // For other block types, initialize empty details object
          details = {};
        }
        return {
          ...block,
          details,
        };
      })
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /**
   * Get appropriate color for block type tags in UI
   * @param blockType - The type of block
   * @returns Element UI tag color string
   */
  function getBlockTypeColor(blockType: string): string {
    const colorMap: { [key: string]: string } = {
      RichText: 'primary',
      Banner: 'success',
      HeroSlideshow: 'warning',
      SearchBar: 'info',
      Collections: 'danger',
      List: '',
      AnnouncementBar: 'warning',
      Question: 'info',
      WelcomeUser: 'success',
      Stickers: 'danger',
      Organizations: 'success',
    };
    return colorMap[blockType] || '';
  }

  /**
   * Get block type display name for better UX
   * @param blockType - The type of block
   * @returns Human-readable block type name
   */
  function getBlockTypeDisplayName(blockType: string): string {
    const displayNameMap: { [key: string]: string } = {
      RichText: 'Rich Text',
      Banner: 'Banner',
      HeroSlideshow: 'Hero Slideshow',
      SearchBar: 'Search Bar',
      Collections: 'Collections',
      List: 'List',
      AnnouncementBar: 'Announcement Bar',
      Question: 'Question',
      WelcomeUser: 'Welcome User',
      Stickers: 'Stickers',
      Organizations: 'Organizations',
    };
    return displayNameMap[blockType] || blockType;
  }

  /**
   * Fetch static page data from API
   * @param idOrSlug - ID or slug of the static page
   * @param isId - Whether the identifier is an ID (true) or slug (false)
   * @returns Promise with static page data
   */
  async function fetchStaticPage(idOrSlug: string, isId = true): Promise<StaticPageData | null> {
    try {
      const response = await makeGraphRequest(
        'query',
        findStaticPage,
        {
          isId,
          idOrSlug,
        },
        getAuthToken(),
      );

      if (!response || !response.data || !response.data.findStaticPage) {
        throw new Error('Static page not found');
      }

      return response.data.findStaticPage;
    } catch (error) {
      console.error('Error fetching static page:', error);
      throw error;
    }
  }

  /**
   * Validate if a block has required details for its type
   * @param block - Processed block to validate
   * @returns Boolean indicating if block is valid
   */
  function isValidBlock(block: ProcessedBlock): boolean {
    if (!block.details) {
      return false;
    }

    switch (block.blockType) {
      case 'RichText': {
        const rtDetails = block.details as RichTextBlockData;
        return !!(rtDetails.heading || rtDetails.text);
      }

      case 'Banner': {
        const bannerDetails = block.details as BannerBlockData;
        return !!(bannerDetails.items && bannerDetails.items.length > 0);
      }

      case 'SearchBar': {
        const searchDetails = block.details as SearchBarBlockData;
        return !!(searchDetails.searchType && searchDetails.placeholderText);
      }

      case 'AnnouncementBar':
      case 'Announcement': {
        const announcementDetails = block.details as AnnouncementBlockData;
        return !!(announcementDetails.text);
      }

      case 'Stickers': {
        const stickersDetails = block.details as StickersBlockData;
        return !!(stickersDetails.title && stickersDetails.columnsCount
          && stickersDetails.stickersList && stickersDetails.stickersList.length > 0);
      }

      case 'Organizations': {
        const organizationsDetails = block.details as OrganizationsBlockData;
        return !!(organizationsDetails.title && organizationsDetails.organizationsList
          && organizationsDetails.organizationsList.length > 0);
      }

      case 'Collections': {
        const collectionsDetails = block.details as CollectionsBlockData;
        return !!(collectionsDetails.title && collectionsDetails.groups
          && collectionsDetails.groups.length > 0);
      }

      case 'Question': {
        const questionDetails = block.details as QuestionBlockData;
        return !!(questionDetails.text && questionDetails.questionId);
        // organizationId is optional, so we don't validate it here
      }

      default:
        return true; // For unknown types, assume valid if details exist
    }
  }

  /**
   * Get block summary for display in lists
   * @param block - Processed block
   * @returns String summary of block content
   */
  function getBlockSummary(block: ProcessedBlock): string {
    if (!block.details) {
      return 'No content available';
    }

    switch (block.blockType) {
      case 'RichText': {
        const rtDetails = block.details as RichTextBlockData;
        return rtDetails.heading || rtDetails.text || 'Empty rich text block';
      }

      case 'Banner': {
        const bannerDetails = block.details as BannerBlockData;
        if (bannerDetails.items && bannerDetails.items.length > 0) {
          const enabledItems = bannerDetails.items.filter((item) => item.isEnabled);
          return `Banner with ${enabledItems.length} enabled image(s)`;
        }
        return 'Empty banner';
      }

      case 'SearchBar': {
        const searchDetails = block.details as SearchBarBlockData;
        return `Search: ${searchDetails.searchType || 'unknown type'}`;
      }

      case 'AnnouncementBar':
      case 'Announcement': {
        const announcementDetails = block.details as AnnouncementBlockData;
        return announcementDetails.text || 'Announcement';
      }

      case 'Stickers': {
        const stickersDetails = block.details as StickersBlockData;
        const count = stickersDetails.stickersList?.length || 0;
        return stickersDetails.title || `Stickers (${count} items)`;
      }

      case 'Organizations': {
        const organizationsDetails = block.details as OrganizationsBlockData;
        const count = organizationsDetails.organizationsList?.length || 0;
        return organizationsDetails.title || `Organizations (${count} items)`;
      }

      case 'Collections': {
        const collectionsDetails = block.details as CollectionsBlockData;
        const groupsCount = collectionsDetails.groups?.length || 0;
        return collectionsDetails.title || `Collections (${groupsCount} groups)`;
      }

      case 'List': {
        const listDetails = block.details as ListBlockData;
        const count = listDetails.organizationsList?.length || 0;
        return listDetails.title || `List (${count} items)`;
      }

      case 'Question': {
        const questionDetails = block.details as QuestionBlockData;
        return questionDetails.text || 'Question';
      }

      default:
        return `${block.blockType} block`;
    }
  }

  /**
   * Delete a static page block
   * @param blockId - ID of the block to delete
   * @param pageId - ID of the static page containing the block
   * @returns Promise
   */
  async function deleteStaticPageBlock(blockId: string, pageId: string): Promise<void> {
    try {
      await makeGraphRequest(
        'mutation',
        mutateRemoveStaticPageBlock,
        {
          blockId,
          id: pageId,
        },
        getAuthToken(),
      );
    } catch (error) {
      console.error('Error deleting static page block:', error);
      throw error;
    }
  }

  /**
   * Delete a static page
   * @param pageId - ID of the static page to delete
   * @returns Promise
   */
  async function deleteStaticPage(pageId: string): Promise<void> {
    try {
      await makeGraphRequest(
        'mutation',
        mutateRemoveStaticPage,
        {
          id: pageId,
        },
        getAuthToken(),
      );
    } catch (error) {
      console.error('Error deleting static page:', error);
      throw error;
    }
  }

  /**
   * Create or update a rich text block
   * @param input - Rich text block data
   * @param staticPageData - Static page data (required for new blocks to calculate sortOrder)
   * @returns Promise with created/updated block data
   */
  async function createOrUpdateRichTextBlock(input: {
    heading: string | null;
    text: string | null;
    parentId: string;
    id?: string | null;
    sortOrder?: number | null;
  }, staticPageData?: StaticPageData): Promise<RichTextBlockData> {
    try {
      // For new blocks (no id), calculate next sortOrder if not provided
      // For existing blocks (with id), use provided sortOrder or keep current
      let finalSortOrder = input.sortOrder;

      if (!input.id && finalSortOrder === null && staticPageData) {
        // New block - get next available sortOrder
        finalSortOrder = getNextSortOrder(staticPageData);
      } else if (finalSortOrder === null) {
        // Fallback to 0 if no sortOrder provided
        finalSortOrder = 0;
      }

      const finalInput = {
        ...input,
        sortOrder: finalSortOrder,
      };

      const response = await makeGraphRequest(
        'mutation',
        mutateRichTextBlock,
        {
          input: finalInput,
        },
        getAuthToken(),
      );

      if (!response || !response.data || !response.data.mutateRichTextBlock) {
        throw new Error('Failed to create/update rich text block');
      }

      return response.data.mutateRichTextBlock;
    } catch (error) {
      console.error('Error creating/updating rich text block:', error);
      throw error;
    }
  }

  /**
   * Create or update a search bar block
   * @param input - Search bar block data
   * @param staticPageData - Static page data (required for new blocks to calculate sortOrder)
   * @returns Promise with created/updated block data
   */
  async function createOrUpdateSearchBarBlock(input: {
    searchType: string | null;
    placeholderText: string | null;
    parentId: string;
    id?: string | null;
    sortOrder?: number | null;
  }, staticPageData?: StaticPageData): Promise<SearchBarBlockData> {
    try {
      // For new blocks (no id), calculate next sortOrder if not provided
      // For existing blocks (with id), use provided sortOrder or keep current
      let finalSortOrder = input.sortOrder;

      if (!input.id && finalSortOrder === null && staticPageData) {
        // New block - get next available sortOrder
        finalSortOrder = getNextSortOrder(staticPageData);
      } else if (finalSortOrder === null) {
        // Fallback to 0 if no sortOrder provided
        finalSortOrder = 0;
      }

      const finalInput = {
        ...input,
        sortOrder: finalSortOrder,
      };

      const response = await makeGraphRequest(
        'mutation',
        mutateSearchBarBlock,
        {
          input: finalInput,
        },
        getAuthToken(),
      );

      if (!response || !response.data || !response.data.mutateSearchBarBlock) {
        throw new Error('Failed to create/update search bar block');
      }

      return response.data.mutateSearchBarBlock;
    } catch (error) {
      console.error('Error creating/updating search bar block:', error);
      throw error;
    }
  }

  /**
   * Create or update an announcement block
   * @param input - Announcement block data
   * @param staticPageData - Static page data (required for new blocks to calculate sortOrder)
   * @returns Promise with created/updated block data
   */
  async function createOrUpdateAnnouncementBlock(input: {
    text: string | null;
    textColor: string | null;
    textBackgroundColor: string | null;
    parentId: string;
    id?: string | null;
    sortOrder?: number | null;
  }, staticPageData?: StaticPageData): Promise<AnnouncementBlockData> {
    try {
      // For new blocks (no id), calculate next sortOrder if not provided
      // For existing blocks (with id), use provided sortOrder or keep current
      let finalSortOrder = input.sortOrder;

      if (!input.id && finalSortOrder === null && staticPageData) {
        // New block - get next available sortOrder
        finalSortOrder = getNextSortOrder(staticPageData);
      } else if (finalSortOrder === null) {
        // Fallback to 0 if no sortOrder provided
        finalSortOrder = 0;
      }

      const finalInput = {
        ...input,
        sortOrder: finalSortOrder,
      };

      const response = await makeGraphRequest(
        'mutation',
        mutateAnnouncementBlock,
        {
          input: finalInput,
        },
        getAuthToken(),
      );

      if (!response || !response.data || !response.data.mutateAnnouncementBlock) {
        throw new Error('Failed to create/update announcement block');
      }

      return response.data.mutateAnnouncementBlock;
    } catch (error) {
      console.error('Error creating/updating announcement block:', error);
      throw error;
    }
  }

  /**
   * Create or update a stickers block
   * @param input - Stickers block data
   * @param staticPageData - Static page data (required for new blocks to calculate sortOrder)
   * @returns Promise with created/updated block data
   */
  async function createOrUpdateStickersBlock(input: {
    title: string | null;
    columnsCount: number | null;
    stickersList: string[] | null;
    parentId: string;
    id?: string | null;
    sortOrder?: number | null;
  }, staticPageData?: StaticPageData): Promise<StickersBlockData> {
    try {
      // For new blocks (no id), calculate next sortOrder if not provided
      // For existing blocks (with id), use provided sortOrder or keep current
      let finalSortOrder = input.sortOrder;

      if (!input.id && finalSortOrder === null && staticPageData) {
        // New block - get next available sortOrder
        finalSortOrder = getNextSortOrder(staticPageData);
      } else if (finalSortOrder === null) {
        // Fallback to 0 if no sortOrder provided
        finalSortOrder = 0;
      }

      const finalInput = {
        ...input,
        sortOrder: finalSortOrder,
      };

      const response = await makeGraphRequest(
        'mutation',
        mutateStickersBlock,
        {
          input: finalInput,
        },
        getAuthToken(),
      );

      if (!response || !response.data || !response.data.mutateStickersBlock) {
        throw new Error('Failed to create/update stickers block');
      }

      return response.data.mutateStickersBlock;
    } catch (error) {
      console.error('Error creating/updating stickers block:', error);
      throw error;
    }
  }

  /**
   * Create or update an organizations block
   * @param input - Organizations block data
   * @param staticPageData - Static page data (required for new blocks to calculate sortOrder)
   * @returns Promise with created/updated block data
   */
  async function createOrUpdateOrganizationsBlock(input: {
    title: string | null;
    organizationsList: string[] | null;
    parentId: string;
    id?: string | null;
    sortOrder?: number | null;
  }, staticPageData?: StaticPageData): Promise<OrganizationsBlockData> {
    try {
      // For new blocks (no id), calculate next sortOrder if not provided
      // For existing blocks (with id), use provided sortOrder or keep current
      let finalSortOrder = input.sortOrder;

      if (!input.id && finalSortOrder === null && staticPageData) {
        // New block - get next available sortOrder
        finalSortOrder = getNextSortOrder(staticPageData);
      } else if (finalSortOrder === null) {
        // Fallback to 0 if no sortOrder provided
        finalSortOrder = 0;
      }

      const finalInput = {
        ...input,
        sortOrder: finalSortOrder,
      };

      const response = await makeGraphRequest(
        'mutation',
        mutateOrganizationsBlock,
        {
          input: finalInput,
        },
        getAuthToken(),
      );

      if (!response || !response.data || !response.data.mutateOrganizationsBlock) {
        throw new Error('Failed to create/update organizations block');
      }

      return response.data.mutateOrganizationsBlock;
    } catch (error) {
      console.error('Error creating/updating organizations block:', error);
      throw error;
    }
  }

  /**
   * Create or update a list block
   * @param input - List block data
   * @param staticPageData - Static page data (required for new blocks to calculate sortOrder)
   * @returns Promise with created/updated block data
   */
  async function createOrUpdateListBlock(input: {
    title: string | null;
    organizationsList: string[] | null;
    parentId: string;
    id?: string | null;
    sortOrder?: number | null;
  }, staticPageData?: StaticPageData): Promise<ListBlockData> {
    try {
      // For new blocks (no id), calculate next sortOrder if not provided
      // For existing blocks (with id), use provided sortOrder or keep current
      let finalSortOrder = input.sortOrder;

      if (!input.id && finalSortOrder === null && staticPageData) {
        // New block - get next available sortOrder
        finalSortOrder = getNextSortOrder(staticPageData);
      } else if (finalSortOrder === null) {
        // Fallback to 0 if no sortOrder provided
        finalSortOrder = 0;
      }

      const finalInput = {
        ...input,
        sortOrder: finalSortOrder,
      };

      const response = await makeGraphRequest(
        'mutation',
        mutateListBlock,
        {
          input: finalInput,
        },
        getAuthToken(),
      );

      if (!response || !response.data || !response.data.mutateListBlock) {
        throw new Error('Failed to create/update list block');
      }

      return response.data.mutateListBlock;
    } catch (error) {
      console.error('Error creating/updating list block:', error);
      throw error;
    }
  }

  /**
   * Create a featured question
   * @param input - Featured question data
   * @returns Promise with created question data
   */
  async function createFeaturedQuestion(input: {
    content: string;
  }): Promise<{ id: string; content: string; createdAt: string }> {
    try {
      const response = await makeGraphRequest(
        'mutation',
        mutateFeaturedQuestion,
        {
          input,
        },
        getAuthToken(),
      );

      if (!response || !response.data || !response.data.mutateFeaturedQuestion) {
        throw new Error('Failed to create featured question');
      }

      return response.data.mutateFeaturedQuestion;
    } catch (error) {
      console.error('Error creating featured question:', error);
      throw error;
    }
  }

  /**
   * Create or update a question block
   * @param input - Question block data
   * @param staticPageData - Static page data (required for new blocks to calculate sortOrder)
   * @returns Promise with created/updated block data
   */
  async function createOrUpdateQuestionBlock(input: {
    questionId: string;
    organizationId: string | null;
    parentId: string;
    id?: string | null;
    sortOrder?: number | null;
  }, staticPageData?: StaticPageData): Promise<QuestionBlockData> {
    try {
      // For new blocks (no id), calculate next sortOrder if not provided
      // For existing blocks (with id), use provided sortOrder or keep current
      let finalSortOrder = input.sortOrder;

      if (!input.id && finalSortOrder === null && staticPageData) {
        // New block - get next available sortOrder
        finalSortOrder = getNextSortOrder(staticPageData);
      } else if (finalSortOrder === null) {
        // Fallback to 0 if no sortOrder provided
        finalSortOrder = 0;
      }

      const finalInput = {
        ...input,
        sortOrder: finalSortOrder,
      };

      const response = await makeGraphRequest(
        'mutation',
        mutateQuestionBlock,
        {
          input: finalInput,
        },
        getAuthToken(),
      );

      if (!response || !response.data || !response.data.mutateQuestionBlock) {
        throw new Error('Failed to create/update question block');
      }

      return response.data.mutateQuestionBlock;
    } catch (error) {
      console.error('Error creating/updating question block:', error);
      throw error;
    }
  }

  /**
   * Create or update a banner block
   * @param input - Banner block data
   * @param staticPageData - Static page data (required for new blocks to calculate sortOrder)
   * @returns Promise with created/updated block data
   */
  async function createOrUpdateBannerBlock(input: {
    setFullWidth: boolean | null;
    items: Array<{
      imageUrl: string;
      deepLink: string;
      isEnabled: boolean;
    }> | null;
    parentId: string;
    id?: string | null;
    sortOrder?: number | null;
  }, staticPageData?: StaticPageData): Promise<BannerBlockData> {
    try {
      // For new blocks (no id), calculate next sortOrder if not provided
      // For existing blocks (with id), use provided sortOrder or keep current
      let finalSortOrder = input.sortOrder;

      if (!input.id && finalSortOrder === null && staticPageData) {
        // New block - get next available sortOrder
        finalSortOrder = getNextSortOrder(staticPageData);
      } else if (finalSortOrder === null) {
        // Fallback to 0 if no sortOrder provided
        finalSortOrder = 0;
      }

      const finalInput = {
        ...input,
        setFullWidth: input.setFullWidth ?? false,
        items: (input.items || []).map((item) => ({
          imageUrl: item.imageUrl,
          deepLink: item.deepLink,
          isEnabled: item.isEnabled,
        })),
        sortOrder: finalSortOrder,
      };

      const response = await makeGraphRequest(
        'mutation',
        mutateBannerBlock,
        {
          input: finalInput,
        },
        getAuthToken(),
      );

      if (!response || !response.data || !response.data.mutateBannerBlock) {
        throw new Error('Failed to create/update banner block');
      }

      return response.data.mutateBannerBlock;
    } catch (error) {
      console.error('Error creating/updating banner block:', error);
      throw error;
    }
  }

  /**
   * Create or update a collections block
   * @param input - Collections block input data
   * @param staticPageData - Optional static page data for calculating sortOrder
   * @returns Promise<CollectionsBlockData>
   */
  async function createOrUpdateCollectionsBlock(input: {
    title: string;
    groups: Array<{
      title: string;
      coverImageUrl: string;
      organizationsList: string[];
      isEnabled: boolean;
    }>;
    parentId: string;
    id?: string | null;
    sortOrder?: number | null;
  }, staticPageData?: StaticPageData): Promise<CollectionsBlockData> {
    try {
      // For new blocks (no id), calculate next sortOrder if not provided
      // For existing blocks (with id), use provided sortOrder or keep current
      let finalSortOrder = input.sortOrder;

      if (!input.id && finalSortOrder === null && staticPageData) {
        // New block - get next available sortOrder
        finalSortOrder = getNextSortOrder(staticPageData);
      } else if (finalSortOrder === null) {
        // Fallback to 0 if no sortOrder provided
        finalSortOrder = 0;
      }

      const finalInput = {
        ...input,
        title: input.title,
        groups: (input.groups || []).map((group) => ({
          title: group.title,
          coverImageUrl: group.coverImageUrl,
          organizationsList: group.organizationsList || [],
          isEnabled: group.isEnabled,
        })),
        sortOrder: finalSortOrder,
      };

      const response = await makeGraphRequest(
        'mutation',
        mutateCollectionsBlock,
        { input: finalInput },
        getAuthToken(),
      );

      if (!response || !response.data || !response.data.mutateCollectionsBlock) {
        throw new Error('Failed to create/update collections block');
      }

      return response.data.mutateCollectionsBlock;
    } catch (error) {
      console.error('Error creating/updating collections block:', error);
      throw error;
    }
  }

  /**
   * Toggle the enabled/disabled status of a static page block
   * @param pageId - ID of the static page containing the block
   * @param blockId - ID of the block to toggle
   * @returns Promise
   */
  async function toggleStaticPageBlockStatus(pageId: string, blockId: string): Promise<void> {
    try {
      await makeGraphRequest(
        'mutation',
        mutateToggleStatusStaticPageBlock,
        {
          id: pageId,
          blockId,
        },
        getAuthToken(),
      );
    } catch (error) {
      console.error('Error toggling static page block status:', error);
      throw error;
    }
  }

  /**
   * Update a single block's sortOrder after drag and drop reordering
   * Backend will automatically handle reordering other blocks
   * @param pageId - ID of the static page
   * @param movedBlock - The block that was moved
   * @param newSortOrder - The new sortOrder position (0-based)
   * @returns Promise
   */
  async function updateBlockOrder(
    pageId: string,
    movedBlock: ProcessedBlock,
    newSortOrder: number,
  ): Promise<void> {
    try {
      const input = {
        id: movedBlock.id,
        sortOrder: newSortOrder,
        parentId: pageId,
      };

      // Call the appropriate mutation based on block type
      switch (movedBlock.blockType) {
        case 'RichText':
          if (movedBlock.details) {
            const details = movedBlock.details as RichTextBlockData;
            await createOrUpdateRichTextBlock({
              ...input,
              heading: details.heading,
              text: details.text,
            });
          }
          break;
        case 'SearchBar':
          if (movedBlock.details) {
            const details = movedBlock.details as SearchBarBlockData;
            await createOrUpdateSearchBarBlock({
              ...input,
              searchType: details.searchType,
              placeholderText: details.placeholderText,
            });
          }
          break;
        case 'AnnouncementBar':
        case 'Announcement':
          if (movedBlock.details) {
            const details = movedBlock.details as AnnouncementBlockData;
            await createOrUpdateAnnouncementBlock({
              ...input,
              text: details.text,
              textColor: details.textColor,
              textBackgroundColor: details.textBackgroundColor,
            });
          }
          break;
        case 'Stickers':
          if (movedBlock.details) {
            const details = movedBlock.details as StickersBlockData;
            await createOrUpdateStickersBlock({
              ...input,
              title: details.title,
              columnsCount: details.columnsCount,
              stickersList: details.stickersList,
            });
          }
          break;
        case 'Organizations':
          if (movedBlock.details) {
            const details = movedBlock.details as OrganizationsBlockData;
            await createOrUpdateOrganizationsBlock({
              ...input,
              title: details.title,
              organizationsList: details.organizationsList,
            });
          }
          break;
        case 'Collections':
          if (movedBlock.details) {
            const details = movedBlock.details as CollectionsBlockData;
            await createOrUpdateCollectionsBlock({
              ...input,
              title: details.title,
              groups: details.groups,
            });
          }
          break;
        case 'List':
          if (movedBlock.details) {
            const details = movedBlock.details as ListBlockData;
            await createOrUpdateListBlock({
              ...input,
              title: details.title,
              organizationsList: details.organizationsList,
            });
          }
          break;
        case 'Question':
          if (movedBlock.details) {
            const details = movedBlock.details as QuestionBlockData;
            await createOrUpdateQuestionBlock({
              ...input,
              questionId: details.questionId,
              organizationId: details.organizationId,
            });
          }
          break;
        case 'Banner':
          if (movedBlock.details) {
            const details = movedBlock.details as BannerBlockData;
            await createOrUpdateBannerBlock({
              ...input,
              setFullWidth: details.setFullWidth ?? false,
              items: (details.items || []).map((item) => ({
                imageUrl: item.imageUrl,
                deepLink: item.deepLink,
                isEnabled: item.isEnabled,
              })),
            });
          }
          break;
        default:
          console.warn(`Unknown block type: ${movedBlock.blockType}`);
          break;
      }
    } catch (error) {
      console.error('Error updating block order:', error);
      throw error;
    }
  }

  return {
    processStaticPageBlocks,
    getBlockTypeColor,
    getBlockTypeDisplayName,
    fetchStaticPage,
    isValidBlock,
    getBlockSummary,
    deleteStaticPageBlock,
    deleteStaticPage,
    createOrUpdateRichTextBlock,
    createOrUpdateSearchBarBlock,
    createOrUpdateAnnouncementBlock,
    createOrUpdateStickersBlock,
    createOrUpdateOrganizationsBlock,
    createOrUpdateListBlock,
    createFeaturedQuestion,
    createOrUpdateQuestionBlock,
    createOrUpdateBannerBlock,
    createOrUpdateCollectionsBlock,
    toggleStaticPageBlockStatus,
    getNextSortOrder,
    updateBlockOrder,
  };
}

export default useStaticPageUtils;
